import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Linkedin, ArrowRight } from "lucide-react";
import ContentHeader from "../components/ContentHeader";
import Footer from "../components/Footer";
import { useSEO } from "../hooks/useSEO";

const SITE = "https://mortgagesurvivalplaybook.com";
const LINKEDIN = "https://www.linkedin.com/company/1660129/";

const About = () => {
  const navigate = useNavigate();
  useSEO(
    "About Lloyd Igbokwe — Mortgage Risk Strategist | Mortgage Survival Playbook 2026",
    "Lloyd Igbokwe is a Houston-based mortgage risk strategist, founder of ENVOLVP LLC, and creator of the Mortgage Survival Playbook 2026 — a foreclosure-prevention framework for servicers.",
    { type: "profile" }
  );

  useEffect(() => {
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-about-ld", "true");
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Person",
      "name": "Lloyd Igbokwe",
      "jobTitle": "Mortgage Risk Strategist",
      "worksFor": { "@type": "Organization", "name": "ENVOLVP LLC", "url": SITE },
      "description": "Houston-based mortgage risk strategist, founder of ENVOLVP LLC, and creator of the Mortgage Survival Playbook 2026. Former executive consultant in AI strategy and value realization at JPMorganChase.",
      "knowsAbout": ["Mortgage servicing", "Foreclosure prevention", "Loss mitigation", "Default management", "Borrower risk segmentation", "Regulation X compliance"],
      "sameAs": [LINKEDIN],
      "url": `${SITE}/about`,
    });
    document.head.appendChild(ld);
    return () => { const s = document.querySelector('script[data-about-ld="true"]'); if (s) s.remove(); };
  }, []);

  return (
    <div style={{ minHeight: "100vh" }} data-testid="about-page">
      <ContentHeader />
      <div className="hero-bg" />
      <article className="article-wrap">
        <div className="eyebrow">About the Creator</div>
        <h1 className="article-title" data-testid="about-title">Lloyd Igbokwe</h1>
        <div className="article-byline">
          Mortgage Risk Strategist · Founder, <span className="gold">ENVOLVP LLC</span> · Houston, Texas
        </div>

        <div className="article-body">
          <p>
            <strong>Lloyd Igbokwe</strong> is the founder of ENVOLVP LLC and the creator of the
            <strong> Mortgage Survival Playbook 2026</strong> — an institutional early-intervention framework
            that helps mortgage servicers, banks, and credit unions identify emerging borrower risk earlier,
            standardize intervention, and prevent avoidable foreclosures.
          </p>
          <p>
            Lloyd previously served as an executive consultant in <strong>AI strategy and value realization
            at JPMorganChase</strong>, where he focused on translating complex data and risk signals into
            practical operating decisions. He brings that same discipline to mortgage servicing: turning
            published market data into structured, repeatable action for default-management teams.
          </p>

          <h2>Why he built the Playbook</h2>
          <p>
            Across the industry, the tools to see borrower distress earlier often exist — but the operating
            structure to act on them consistently does not. Lloyd developed the Playbook to close that gap:
            a self-contained framework built from analysis of <strong>ATTOM U.S. foreclosure market data</strong>,
            <strong> Mortgage Bankers Association delinquency surveys</strong>, and <strong>CFPB Regulation X</strong>
            servicing requirements, organized into a methodology institutions can deploy within their own systems
            and governance.
          </p>

          <h2>Areas of focus</h2>
          <ul>
            <li>Early borrower-risk identification and 4-tier segmentation</li>
            <li>Structured intervention and loss-mitigation protocols</li>
            <li>Servicing governance, documentation, and Regulation X alignment</li>
            <li>Portfolio measurement, KPIs, and default-prevention ROI</li>
          </ul>

          <h2>The mission</h2>
          <p>
            Keep more families in their homes while protecting institutional portfolio value — by acting earlier,
            engaging consistently, and governing every decision. The Playbook is updated as new market data is
            published, and every licensee receives 90 days of direct implementation support.
          </p>
          <p style={{ fontSize: "0.85rem", color: "var(--g3)" }}>
            The Mortgage Survival Playbook is an operational framework and is not legal, regulatory, or investment advice.
          </p>
        </div>

        <div className="article-cta" data-testid="about-cta">
          <h3>Explore the Framework</h3>
          <p>See what's included in the institutional license, or connect on LinkedIn.</p>
          <div style={{ display: "flex", gap: "0.7rem", justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => navigate("/#purchase")} data-testid="about-explore-btn">
              View the Institutional License <ArrowRight size={15} style={{ display: "inline", verticalAlign: "middle" }} />
            </button>
            <a href={LINKEDIN} target="_blank" rel="noopener noreferrer" className="audio-btn audio-btn-ghost" style={{ textDecoration: "none" }} data-testid="about-linkedin">
              <Linkedin size={15} /> LinkedIn
            </a>
          </div>
        </div>
      </article>
      <Footer />
    </div>
  );
};

export default About;
